import { filterPrematchOffers, oddsTime } from "./prematchOdds";
const MARKET_CONFIG =
  {
    "double-chance": { marketId: 2, outcomes: {"1X":"1X", "12":"12", "X2":"X2"} },
    "1x2": {
      marketId: 1,
      outcomes: {
        1: "Home",
        X: "Draw",
        2: "Away",
      },
    },
    "over-under": {
      marketId: 80,
      outcomes: {
        over: "Over",
        under:
          "Under",
      },
      lineField:
        "total",
    },
    "asian-handicap": {
      marketId: 6,
      outcomes: {
        home: "Home",
        away: "Away",
      },
      lineField: "handicap",
    },
    btts: {
      marketId: 14,
      outcomes: {
        yes: "Yes",
        no: "No",
      },
    },
  };

export function canonicalOddsLine(
  value
) {
  const text =
    String(
      value ?? ""
    ).trim();
  if (
    !/^[+-]?\d+(?:\.\d+)?$/.test(
      text
    )
  )
    return null;
  const number =
    Number(text);
  return Number.isFinite(
    number
  )
    ? number.toFixed(
        2
      )
    : null;
}

export function canonicalAsianHandicapLine(value) {
  const text = String(value ?? "").trim().replace(/\s+/g, "");
  const numeric = canonicalOddsLine(text);
  if (numeric !== null) return numeric;

  const parts = text.split(/[,/]/);
  if (parts.length !== 2) return null;
  const values = parts.map((part) => Number(part));
  if (!values.every(Number.isFinite)) return null;
  const difference = Math.abs(values[0] - values[1]);
  return Math.abs(difference - 0.5) < 1e-12
    ? ((values[0] + values[1]) / 2).toFixed(2)
    : null;
}

// Sportmonks' named Home/Away offers retain the common market group in
// original_label (even when handicap has already been inverted for Away).
// Numeric 1/2 offers, e.g. bet365, instead carry selection-relative lines.
// Legacy records without the original group keep their existing perspective.
export function asianHandicapHomeLine(odd) {
  const side = String(odd?.label || "");
  if (["Home", "Away"].includes(side)) {
    const group = canonicalAsianHandicapLine(odd?.original_label);
    if (group !== null) return group;
  }
  const supplied = canonicalAsianHandicapLine(odd?.handicap);
  if (supplied === null || !["Home", "Away", "1", "2"].includes(side)) return null;
  return ["Home", "1"].includes(side) ? supplied : (-Number(supplied)).toFixed(2);
}

export function readAggregatedMarketOdd(
  result,
  outcome,
  basis
) {
  const value = Number(
    result?.outcomes?.[outcome]?.[basis]
  );
  return Number.isFinite(value) && value > 0
    ? value
    : null;
}

export function hasOddsIndication(fairOdd, marketOdd, thresholdPercent) {
  const fair = Number(fairOdd);
  const market = Number(marketOdd);
  const threshold = Number(thresholdPercent);
  return Number.isFinite(fair) && fair > 0 &&
    Number.isFinite(market) && market > 0 &&
    Number.isFinite(threshold) &&
    (market / fair - 1) * 100 >= threshold;
}

// A tip needs the same outcome, line and market quote to qualify in BOTH models.
// Missing statistical predictions never fall back to the results model.
export function hasConsensusOddsIndication(resultOdd, statisticOdd, marketOdd, thresholdPercent) {
  return hasOddsIndication(resultOdd, marketOdd, thresholdPercent) &&
    hasOddsIndication(statisticOdd, marketOdd, thresholdPercent);
}

export function aggregateFixtureMarketOdds(
  records,
  market,
  {
    line = null,
  } = {}
) {
  records = filterPrematchOffers(records).map(odd => ({...odd,
    market_id: {7:80,81:80,105:80,104:6}[odd.market_id] || odd.market_id,
    label: Number(odd.market_id) === 2 ? ({"Home/Draw":"1X","Home/Away":"12","Draw/Away":"X2","1/X":"1X","1/2":"12","X/2":"X2"}[odd.label] || odd.label) : odd.label,
  }));
  // Equivalent provider markets can repeat the same bookmaker/side/line.
  // Use the same latest-per-book rule as Marvin rather than overweighting it.
  if (["asian-handicap", "over-under"].includes(market)) {
    const books = new Map();
    records = records.filter(odd => odd?.stopped !== true && Number(odd?.value) > 0);
    for (const [index, odd] of records.entries()) {
      const book = Number(odd.bookmaker_id ?? odd.bookmaker?.id);
      const side = market === "asian-handicap"
        ? ({Home:"home", Away:"away", 1:"home", 2:"away"}[odd.label])
        : String(odd.label || "").toLowerCase();
      const group = market === "asian-handicap" ? asianHandicapHomeLine(odd) : canonicalAsianHandicapLine(odd.total);
      const key = book > 0 ? `${odd.market_id}:${book}:${side}:${group}` : `unknown:${index}`;
      const old = books.get(key);
      if (!old || oddsTime(odd.latest_bookmaker_update) > oddsTime(old.latest_bookmaker_update)) books.set(key, odd);
    }
    records = [...books.values()];
  }
  const config =
    MARKET_CONFIG[
      market
    ];
  if (!config)
    return null;

  if (market === "over-under") {
    const valuesByLine = new Map();
    for (const odd of Array.isArray(records) ? records : []) {
      const oddLine = canonicalAsianHandicapLine(odd?.total);
      const side = String(odd?.label || "").toLowerCase();
      const value = Number(odd?.value);
      if (
        Number(odd?.market_id) !== 80 ||
        odd?.stopped === true ||
        !oddLine ||
        !["over", "under"].includes(side) ||
        !Number.isFinite(value) ||
        value <= 0
      ) continue;
      if (!valuesByLine.has(oddLine)) {
        valuesByLine.set(oddLine, { over: [], under: [] });
      }
      valuesByLine.get(oddLine)[side].push(value);
    }

    const lines = {};
    for (const [oddLine, values] of valuesByLine) {
      const stats = (side) => ({
        average: values[side].length
          ? values[side].reduce((sum, odd) => sum + odd, 0) /
            values[side].length
          : null,
        highest: values[side].length ? Math.max(...values[side]) : null,
        count: values[side].length,
      });
      const over = stats("over");
      const under = stats("under");
      const hasBothSides = over.count > 0 && under.count > 0;
      const overProbability = hasBothSides ? 1 / over.average : null;
      const underProbability = hasBothSides ? 1 / under.average : null;
      lines[oddLine] = {
        outcomes: { over, under },
        balanceDistance: hasBothSides
          ? Math.abs(
              overProbability / (overProbability + underProbability) - 0.5
            )
          : null,
      };
    }

    const ranked = Object.keys(lines)
      .filter((oddLine) => Number.isFinite(lines[oddLine].balanceDistance))
      .sort((a, b) => lines[a].balanceDistance - lines[b].balanceDistance);
    const unique = ranked.length < 2 ||
      Math.abs(lines[ranked[0]].balanceDistance - lines[ranked[1]].balanceDistance) > 1e-12;
    const mostBalancedLine = unique ? ranked[0] || null : null;
    const wantedLine = line === "market-balanced"
      ? mostBalancedLine
      : canonicalAsianHandicapLine(line);

    return {
      market,
      line: wantedLine,
      outcomes: lines[wantedLine]?.outcomes || {
        over: { average: null, highest: null, count: 0 },
        under: { average: null, highest: null, count: 0 },
      },
      availableLines: Object.keys(lines).sort((a, b) => Number(a) - Number(b)),
      mostBalancedLine,
    };
  }
  if (market === "asian-handicap") {
    const valuesByLine = new Map();
    for (const odd of Array.isArray(records) ? records : []) {
      if (
        Number(odd?.market_id) !== 6 ||
        odd?.stopped === true ||
        !["Home", "Away", "1", "2"].includes(String(odd?.label))
      ) continue;
      const outcome = ["Home", "1"].includes(String(odd.label))
        ? "home"
        : "away";
      const homeLine = asianHandicapHomeLine(odd);
      const value = Number(odd?.value);
      if (!homeLine || !Number.isFinite(value) || value <= 0) continue;
      if (!valuesByLine.has(homeLine)) {
        valuesByLine.set(homeLine, { home: [], away: [] });
      }
      valuesByLine.get(homeLine)[outcome].push(value);
    }

    const emptyOutcome = () => ({ average: null, highest: null, count: 0 });
    const stats = (values) => ({
      average: values.length
        ? values.reduce((sum, odd) => sum + odd, 0) / values.length
        : null,
      highest: values.length ? Math.max(...values) : null,
      count: values.length,
    });
    const lines = Object.fromEntries(
      [...valuesByLine].map(([homeLine, values]) => [
        homeLine,
        { home: stats(values.home), away: stats(values.away) },
      ])
    );
    const wantedLine = canonicalAsianHandicapLine(line);
    return {
      market,
      line: wantedLine,
      outcomes: lines[wantedLine] || {
        home: emptyOutcome(),
        away: emptyOutcome(),
      },
      availableLines: Object.keys(lines).sort((a, b) => Number(a) - Number(b)),
    };
  }
  const wantedLine =
    config.lineField
      ? canonicalOddsLine(
          line
        )
      : null;
  if (
    config.lineField &&
    wantedLine ===
      null
  )
    return null;

  const outcomes =
    Object.fromEntries(
      Object.entries(
        config.outcomes
      ).map(
        ([
          key,
          label,
        ]) => {
          const values =
            (
              Array.isArray(
                records
              )
                ? records
                : []
            )
              .filter(
                (
                  odd
                ) =>
                  Number(
                    odd?.market_id
                  ) ===
                    config.marketId &&
                  odd?.stopped !==
                    true &&
                  odd?.label ===
                    label &&
                  (!config.lineField ||
                    canonicalOddsLine(
                      odd?.[
                        config
                          .lineField
                      ]
                    ) ===
                      wantedLine)
              )
              .map(
                (
                  odd
                ) =>
                  Number(
                    odd?.value
                  )
              )
              .filter(
                (
                  value
                ) =>
                  Number.isFinite(
                    value
                  ) &&
                  value >
                    0
              );
          return [
            key,
            {
              average:
                values.length
                  ? values.reduce(
                      (
                        sum,
                        value
                      ) =>
                        sum +
                        value,
                      0
                    ) /
                    values.length
                  : null,
              highest:
                values.length
                  ? Math.max(
                      ...values
                    )
                  : null,
              count:
                values.length,
            },
          ];
        }
      )
    );

  return {
    market,
    line: wantedLine,
    outcomes,
  };
}
